<template>
  <v-row>
    <v-col>
      <div v-if="facilityData" class="facility">
        <IsOwnerIndicator :isOwner="isOwner" height="24px" class="mx-3" />
        <div class="facility__content-primary">
          <p class="facility__name">{{ facilityName }}</p>
          <div class="facility__description">
            <p class="facility__address">
              {{ facilityDescription }}
            </p>
          </div>
        </div>
      </div>

      <v-row>
        <v-col>
          <Pagination
            class="flex-grow-1"
            :page="page"
            :per-page="perPage"
            :options="perPageOptions"
            :total-count="totalCount"
            @change="handleChange"
          ></Pagination>
        </v-col>
      </v-row>

      <div class="changes" v-if="changes.length > 0">
        <div class="changes__list-header">
          <v-row>
            <v-col cols="2">
              {{ $t("message.externalChanges.date") }}
            </v-col>
            <v-col cols="2">
              {{ $t("message.externalChanges.origin") }}
            </v-col>
            <v-col cols="2">
              {{ $t("message.externalChanges.action") }}
            </v-col>
            <v-col cols="6">
              <span>
                {{ $t("message.externalChanges.change") }}
              </span>
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-icon small color="accent" v-on="on" class="ml-1 mb-1">
                    mdi-information
                  </v-icon>
                </template>
                <span>{{
                  $t("message.externalChanges.history.changeHeaderTooltip")
                }}</span>
              </v-tooltip>
            </v-col>
          </v-row>
        </div>
        <div class="changes__list">
          <ChangeListItem
            v-for="change in changes"
            :key="change.id"
            :change="change"
            :isHistoryMode="true"
            class="changes__list-item"
          />
          <SectionLoader :status="loading.list" :opacity="0.5" />
        </div>
      </div>
      <div class="no-changes" v-else-if="loaded && changes.length === 0">
        <img
          src="@/static/externalChanges/noChanges.svg"
          :alt="$t('message.externalChanges.noChangesAlt')"
        />
        <p class="no-groups__description">
          {{ $t("message.externalChanges.noChangesHistory") }}
        </p>
      </div>
    </v-col>
    <SectionLoader :status="loading.view" :opacity="1" />
  </v-row>
</template>

<script>
import IsOwnerIndicator from "@/components/Permissions/IsOwnerIndicator.vue";
import ChangeListItem from "@/components/ExternalChanges/ChangeListItem.vue";
import SectionLoader from "@/components/Loader/SectionLoader.vue";
import Pagination from "@/components/List/Pagination";

import { EXTERNAL_CHANGES_STATUS } from "@/enums/external-changes";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import serviceHandler from "@/helpers/handlers/service";
import facilitiesService from "@/services/facilities";
import gmbExternalChangesService from "@/services/externalChanges";
import { FEATURE_FLAG, isFeatureFlagEnabled } from "@/helpers/posthog";

export default {
  name: "FacilityExternalChangesHistory",
  components: {
    IsOwnerIndicator,
    ChangeListItem,
    SectionLoader,
    Pagination
  },

  data() {
    return {
      loaded: false,
      loading: {
        view: false,
        list: false
      },
      facilityData: null,
      changes: [],
      EXTERNAL_CHANGES_STATUS,
      totalCount: 0,
      page: 1,
      perPageOptions: [10, 25, 50, 100],
      perPage: null
    };
  },

  computed: {
    facilityDescription() {
      const storeLabel = this.facilityData.store_code
        ? ` | ${this.$t("message.common.storeCode")}: ${
            this.facilityData.store_code
          }`
        : "";
      return `${this.facilityData.formatted_address}${storeLabel}`;
    },
    isOwner() {
      return this.facilityData.facility_listings.is_owner;
    },
    facilityName() {
      return this.facilityData.name;
    }
  },

  async created() {
    this.loading.view = true;
    this.perPage = this.perPageOptions[0];

    await this.fetchFacilityData();
    await this.fetchExternalChanges();
    this.loaded = true;
    this.loading.view = false;

    this.$gtm.trackCustomEvent({
      event: GTM_EVENT_NAME.LOCATION_DETAILS_ACTION,
      location_id: this.facilityData.id,
      action: "tab_ext_changes_history"
    });
  },

  methods: {
    async fetchExternalChanges() {
      this.loading.list = true;
      const response = await gmbExternalChangesService.fetchExternalChanges({
        page: this.page,
        perPage: this.perPage,
        facilitiesIds: [this.$route.params.id],
        statuses: [
          EXTERNAL_CHANGES_STATUS.ACCEPTED,
          EXTERNAL_CHANGES_STATUS.REJECTED
        ]
      });

      this.changes =
        response &&
        response.data &&
        response.data.results &&
        response.data.results.length > 0
          ? response.data.results
          : [];

      if (
        !isFeatureFlagEnabled(FEATURE_FLAG.SHOW_SERVICE_ITEMS_EXTERNAL_CHANGES)
      ) {
        this.changes = this.changes.filter(
          change => change.field.value !== "service_items"
        );
      }

      this.totalCount =
        response && response.data && response.data.count
          ? response.data.count
          : 0;
      this.loading.list = false;
    },

    async fetchFacilityData() {
      const data = await serviceHandler(
        facilitiesService.getFacilityData(this.$route.params.id)
      );

      this.facilityData = data || null;
    },

    async handleChange(
      { page, perPage } = { page: this.page, perPage: this.perPage }
    ) {
      this.page = page;
      this.perPage = perPage;
      await this.fetchExternalChanges();
    }
  }
};
</script>

<style lang="scss" scoped>
.facility {
  display: flex;
  align-items: center;
  margin-bottom: $spacing * 3.5;

  &__content-primary {
    max-width: calc(100% - 245px);
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &__name,
  &__description {
    color: $dark;
  }

  &__name {
    @include font-size(14px);
    font-weight: 500;
    margin-top: $spacing * 0.5;
    margin-bottom: 0;
  }

  &__description {
    position: relative;
    display: flex;
    @include font-size(12px);
    font-weight: 400;
    margin-bottom: 0;
    max-width: 100%;
    white-space: nowrap;
    color: $text;

    & > p {
      margin-bottom: 0;
    }
  }

  &__address {
    margin-right: $spacing;
    position: relative;
    text-overflow: ellipsis;
    overflow: hidden;
  }
}

.changes {
  &__list,
  &__list-header {
    padding: 0 $spacing * 4.5;
  }

  &__list-header {
    @include font-size(12px);
    opacity: 0.6;
  }

  &__list {
    border-radius: 25px;
    background-color: $light-4;
    position: relative;
  }

  &__list-item:not(:last-child) {
    border-bottom: 1px solid $light-3;
  }
}

.no-changes {
  @include noData(450px);

  &__description {
    @include font-size(22px);
    text-align: center;
    margin: $spacing * 5 0 $spacing * 3;
  }
}
</style>
